import {
  Download,
  Wand2,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  FileArchive,
  Palette,
  Box,
  ShoppingCart,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import SizePlacementAssistant from "../components/artwork/SizePlacementAssistant";

const installSteps = [
  "Download the DTF Wizard Artwork Prep Actions zip file.",
  "Extract the zip file to your computer.",
  "Open Adobe Photoshop.",
  "Go to Window > Actions.",
  "Open the Actions panel menu in the top right.",
  "Click Load Actions.",
  "Select: DTF Wizard Prep Actions.atn",
  "The action set will appear in your Actions panel.",
  "Open an image and press Play on the action you want to run.",
];

const safetyTips = [
  "Always work on a duplicate of your artwork file.",
  "Keep your original file untouched.",
  "Review results before sending to print.",
  "Some actions may flatten or modify layers.",
  "Try different tools to compare results.",
];

const startGuide = [
  {
    title: "Need a quick result?",
    description: "Start with Auto Prep for the fastest workflow.",
  },
  {
    title: "Need cleanup?",
    description:
      "Use Transparency Tools for edge cleanup and transparency work.",
  },
  {
    title: "Need effects or fades?",
    description:
      "Use Halftone Tools for fades, patterns, and creative output.",
  },
  {
    title: "Need a print-ready white layer?",
    description: "Use White Underbase Tools before sending to print.",
  },
];

const colorCharts = [
  {
    title: "DTF Color Chart 1",
    description:
      "Printable CMYK reference chart for comparing output and color behavior.",
    preview: "/downloads/cmyk/cmyk-chart-1.png",
    file: "/downloads/cmyk/cmyk-chart-1.pdf",
    buttonLabel: "Download PDF",
  },
  {
    title: "DTF Color Chart 2",
    description:
      "Second printable CMYK reference chart for shop testing and comparison.",
    preview: "/downloads/cmyk/cmyk-chart-2.png",
    file: "/downloads/cmyk/cmyk-chart-2.pdf",
    buttonLabel: "Download PDF",
  },
];

const printableAids = [
  {
    title: "Film Guides (Standard)",
    description:
      "Printable film guide set for standard alignment help and workflow support.",
    file: "/downloads/stl/film-guides.zip",
    preview: "/downloads/stl/film-guides.png",
    buyLink: "https://apexdesignsllc.com/product/film-guides/",
    tag: "STL Download",
  },
  {
    title: "Film Guides (Viewfinder)",
    description:
      "Printable film guide set with viewfinder cutouts for easier placement reference.",
    file: "/downloads/stl/film-guides-viewfinder.zip",
    preview: "/downloads/stl/film-guides-viewfinder.png",
    buyLink: "https://apexdesignsllc.com/product/film-guides/",
    tag: "STL Download",
  },
  {
    title: "Powder Keeper Discs",
    description:
      "Printable discs designed to help manage powder handling during your workflow.",
    file: "/downloads/stl/powder-keeper-discs.zip",
    preview: "/downloads/stl/powder-keeper-discs.png",
    buyLink: "https://apexdesignsllc.com/product/powder-keeper-discs/",
    tag: "STL Download",
  },
  {
    title: "Damper Holder",
    description:
      "Printable holder for organizing dampers during printer service or maintenance.",
    file: "/downloads/stl/damper-holder.zip",
    preview: "/downloads/stl/damper-holder.png",
    buyLink: "https://apexdesignsllc.com/product/i3200-xp600-damper-holder/",
    tag: "STL Download",
  },
];

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  eyebrow,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-dtf-blue">
        <Icon className="h-4 w-4" />
        {eyebrow}
      </div>

      <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl">
        {title}
      </h2>

      <p className="mt-3 max-w-3xl text-sm leading-6 text-dtf-text-muted md:text-base">
        {description}
      </p>
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
      <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-dtf-text-muted">
        <span className="text-dtf-accent">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  );
}

function DownloadButton({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  return (
    <a
      href={href}
      download
      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-4 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18"
    >
      <Download className="h-4 w-4" />
      {label}
    </a>
  );
}

function BuyNowButton({
  href,
}: {
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-dtf-accent/25 bg-dtf-accent/10 px-4 py-3 text-sm font-semibold text-dtf-accent transition hover:-translate-y-0.5 hover:border-dtf-accent/45 hover:bg-dtf-accent/15"
    >
      <ShoppingCart className="h-4 w-4" />
      Buy Now
    </a>
  );
}

function FeatureCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-xl border border-dtf-blue/20 bg-dtf-blue/10 p-2 shadow-[0_0_12px_rgba(59,130,246,0.18)]">
          <CheckCircle2 className="h-4 w-4 text-dtf-blue" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <p className="mt-2 text-sm leading-6 text-dtf-text-muted">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function InstallStepCard({
  step,
  index,
}: {
  step: string;
  index: number;
}) {
  return (
    <div className="flex gap-3 rounded-2xl border border-white/10 bg-black/20 p-4 shadow-[0_0_14px_rgba(37,99,235,0.06)]">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-dtf-blue/20 bg-dtf-blue/10 text-sm font-bold text-dtf-blue">
        {index + 1}
      </div>
      <p className="text-sm leading-6 text-dtf-text">{step}</p>
    </div>
  );
}

function SafetyTipCard({ tip }: { tip: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="mt-0.5 rounded-xl border border-dtf-accent/20 bg-dtf-accent/10 p-2">
        <AlertTriangle className="h-4 w-4 text-dtf-accent" />
      </div>
      <p className="text-sm leading-6 text-dtf-text">{tip}</p>
    </div>
  );
}

function ToolCard({
  title,
  description,
  preview,
  file,
  buyLink,
  tag,
}: {
  title: string;
  description: string;
  preview: string;
  file: string;
  buyLink: string;
  tag: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] transition duration-300 hover:-translate-y-1 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10 bg-black/30">
        <img
          src={preview}
          alt={title}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
        />
        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center rounded-full border border-dtf-blue/20 bg-[#08111f]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-dtf-blue backdrop-blur">
            {tag}
          </span>
        </div>
      </div>

      <div className="relative p-5 md:p-6">
        <h3 className="text-xl font-extrabold tracking-tight text-white">
          {title}
        </h3>

        <p className="mt-3 text-sm leading-6 text-dtf-text">{description}</p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <DownloadButton href={file} label="Download STL" />
          <BuyNowButton href={buyLink} />
        </div>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  description,
  preview,
  file,
  buttonLabel,
}: {
  title: string;
  description: string;
  preview: string;
  file: string;
  buttonLabel: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-[26px] border border-white/10 bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.14),transparent_48%),linear-gradient(180deg,rgba(9,21,44,0.98)_0%,rgba(7,15,31,0.98)_100%)] transition duration-300 hover:-translate-y-1 hover:border-dtf-blue/30 hover:shadow-[0_0_26px_rgba(37,99,235,0.18)]">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.06),transparent_35%,transparent_65%,rgba(37,99,235,0.04))]" />

      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10 bg-black/30">
        <img
          src={preview}
          alt={title}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
        />
        <div className="absolute left-4 top-4">
          <span className="inline-flex items-center rounded-full border border-emerald-400/20 bg-[#08111f]/85 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-300 backdrop-blur">
            Reference Chart
          </span>
        </div>
      </div>

      <div className="relative p-5 md:p-6">
        <h3 className="text-xl font-extrabold tracking-tight text-white">
          {title}
        </h3>

        <p className="mt-3 text-sm leading-6 text-dtf-text">{description}</p>

        <div className="mt-5">
          <DownloadButton href={file} label={buttonLabel} />
        </div>
      </div>
    </div>
  );
}

export default function ArtworkPrepActions() {
  const downloadHref = "/downloads/DTF-Wizard-Artwork-Prep-Actions.zip";

  return (
    <div className="min-h-screen bg-[#070b14] text-white">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        {/* HERO */}
        <section className="relative">
          <BluePanel className="p-6 md:p-10">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(59,130,246,0.25),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_28%)]" />

            <div className="relative grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/25 bg-dtf-blue/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-dtf-blue">
                  <Sparkles className="h-4 w-4" />
                  Prep Toolkit
                </div>

                <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-5xl">
                  DTF Wizard Artwork Prep Toolkit
                </h1>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-dtf-text md:text-base">
                  Speed up artwork prep with ready-to-use Photoshop actions,
                  printable CMYK reference charts, downloadable shop tools, and
                  fast size guidance for better print prep.
                </p>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={downloadHref}
                    download
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-dtf-blue/35 bg-dtf-blue/12 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/60 hover:bg-dtf-blue/18"
                  >
                    <Download className="h-4 w-4" />
                    Download Action Pack
                  </a>

                  <a
                    href="#printable-tools"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/20 px-5 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:border-dtf-blue/30 hover:bg-black/30"
                  >
                    View Printable Tools
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {startGuide.map((item) => (
                  <FeatureCard
                    key={item.title}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>
          </BluePanel>
        </section>

        {/* ACTION PACK + STATS */}
        <section className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <BluePanel className="p-6">
            <SectionTitle
              icon={FileArchive}
              eyebrow="Actions"
              title="Download the Photoshop action pack"
              description="A quick way to speed up artwork prep inside Photoshop."
            />

            <div className="rounded-[24px] border border-white/10 bg-black/20 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-dtf-blue">
                    <Wand2 className="h-3.5 w-3.5" />
                    Action Pack
                  </div>

                  <h3 className="mt-3 text-xl font-extrabold text-white">
                    DTF Wizard Prep Actions
                  </h3>

                  <p className="mt-2 max-w-2xl text-sm leading-6 text-dtf-text">
                    Includes tools for faster prep, cleanup, transparency work,
                    fades, and white underbase workflow support.
                  </p>
                </div>

                <div className="w-full md:w-auto md:min-w-[220px]">
                  <DownloadButton href={downloadHref} label="Download ZIP" />
                </div>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <StatPill
                icon={<Wand2 className="h-4 w-4" />}
                label="Main Resource"
                value="Action Pack"
              />
              <StatPill
                icon={<Palette className="h-4 w-4" />}
                label="Color Charts"
                value={`${colorCharts.length}`}
              />
              <StatPill
                icon={<Box className="h-4 w-4" />}
                label="Printable Tools"
                value={`${printableAids.length}`}
              />
            </div>
          </BluePanel>

          <BluePanel className="p-6">
            <SectionTitle
              icon={ShieldCheck}
              eyebrow="Important"
              title="Before you run actions"
              description="These are fast tools, but you should still check the result before sending files to print."
            />

            <div className="grid gap-3">
              {safetyTips.map((tip) => (
                <SafetyTipCard key={tip} tip={tip} />
              ))}
            </div>
          </BluePanel>
        </section>

        {/* INSTALL */}
        <section className="mt-10">
          <BluePanel className="p-6">
            <SectionTitle
              icon={Wand2}
              eyebrow="Install"
              title="How to install in Photoshop"
              description="Load the action file once, then run it whenever needed."
            />

            <div className="grid gap-3">
              {installSteps.map((step, index) => (
                <InstallStepCard key={step} step={step} index={index} />
              ))}
            </div>
          </BluePanel>
        </section>

        {/* SIZE & PLACEMENT ASSISTANT */}
        <section className="mt-10">
          <BluePanel className="p-6">
            <SizePlacementAssistant />
          </BluePanel>
        </section>

        {/* CHARTS */}
        <section className="mt-10">
          <SectionTitle
            icon={Palette}
            eyebrow="Charts"
            title="CMYK Reference Charts"
            description="Preview and download printable charts for testing, comparison, and color reference."
          />

          <div className="grid gap-6 md:grid-cols-2">
            {colorCharts.map((chart) => (
              <ChartCard key={chart.title} {...chart} />
            ))}
          </div>
        </section>

        {/* PRINTABLE TOOLS */}
        <section id="printable-tools" className="mt-10">
          <SectionTitle
            icon={Box}
            eyebrow="Tools"
            title="3D Printable Shop Tools"
            description="Download STL files to print your own tools, or grab the physical version using the Buy Now button."
          />

          <div className="grid gap-6 md:grid-cols-2">
            {printableAids.map((item) => (
              <ToolCard key={item.title} {...item} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}